import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "./operational.less";
import {
  Invit,
  YearDataOperationalMetrics,
} from "../../../../types/invit.types";
import { formatNumber, safeFormat } from "../../../../utils/financialHelper";

interface OperationalProps {
  invit: Invit | null;
}

interface MetricsRow {
  metric: string;
  value: string | number;
  project: string;
}

const Operational: React.FC<OperationalProps> = ({ invit }) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const [year, setYear] = useState("");
  const [semiAnnual, setSemiAnnual] = useState("");
  const [projects, setProjects] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<Record<string, MetricsRow[]>>({});
  const [years, setYears] = useState<string[]>([]);
  const [semiAnnuals, setSemiAnnuals] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toStringSafe = (val: unknown): string => {
    if (val === null || val === undefined) return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return String(val);
  };

  useEffect(() => {
    if (!invit?.operationalMetrics) return;

    // Extract all year labels
    const yearList = invit.operationalMetrics.map((y) => y.year);

    // Sort descending by numeric year (e.g., "FY 2024-25" → 2024)
    const sortedYears = [...yearList].sort((a, b) => {
      const yearA = parseInt(a.match(/\d{4}/)?.[0] || "0", 10);
      const yearB = parseInt(b.match(/\d{4}/)?.[0] || "0", 10);
      return yearB - yearA;
    });

    setYears(sortedYears);
    setYear(sortedYears[0] || ""); // ✅ latest year as default
  }, [invit]);

  useEffect(() => {
    if (!year || !invit?.operationalMetrics) return;
    const yearData = invit.operationalMetrics.find(
      (y) => y.year === year
    ) as YearDataOperationalMetrics;
    if (!yearData) return;

    const uniqueSemiAnnuals = Array.from(
      new Set(yearData.semiAnnuals.map((s) => s.semiAnnual))
    ).sort();
    setSemiAnnuals(uniqueSemiAnnuals);
    setSemiAnnual(uniqueSemiAnnuals[0] || "");
  }, [year, invit]);

  useEffect(() => {
    if (!year || !semiAnnual || !invit?.operationalMetrics) return;
    const yearData = invit.operationalMetrics.find(
      (y) => y.year === year
    ) as YearDataOperationalMetrics;
    if (!yearData) return;

    const allProjects = yearData.semiAnnuals
      .filter((s) => s.semiAnnual === semiAnnual)
      .map((s) => s.sectorOfOperation.nameOfProject?.value)
      .filter(Boolean) as string[];

    const uniqueProjects = Array.from(new Set(allProjects)).sort();
    setProjects(uniqueProjects);

    // 🔹 Preserve previously selected projects if they exist
    setSelectedProjects((prev) => {
      const validPrev = prev.filter((p) => uniqueProjects.includes(p));
      if (validPrev.length > 0) return validPrev;
      return uniqueProjects.slice(0, 1);
    });
  }, [semiAnnual, year, invit]);

  useEffect(() => {
    if (
      !year ||
      !semiAnnual ||
      selectedProjects.length === 0 ||
      !invit?.operationalMetrics
    )
      return;

    const yearData = invit.operationalMetrics.find(
      (y) => y.year === year
    ) as YearDataOperationalMetrics;
    if (!yearData) return;

    const metricsMap: Record<string, MetricsRow[]> = {};

    yearData.semiAnnuals
      .filter((s) => s.semiAnnual === semiAnnual)
      .forEach((semi) => {
        const sector = semi.sectorOfOperation;
        const projectName =
          toStringSafe(sector.nameOfProject?.value) || "Unknown Project";
        if (!selectedProjects.includes(projectName)) return;

        const rows: MetricsRow[] = [];

        Object.entries(sector).forEach(([key, val]) => {
          if (key === "name" || typeof val !== "object" || val === null) return;

          if ("key" in val && "value" in val) {
            rows.push({
              metric: (val as any).key,
              value:
                typeof (val as any).value === "number"
                  ? formatNumber((val as any).value)
                  : toStringSafe((val as any).value),
              project: projectName,
            });
          } else if (key === "revenue") {
            const revenue = val as any;
            Object.keys(revenue).forEach((rKey) => {
              const record = revenue[rKey];
              if (record?.key) {
                rows.push({
                  metric: record.key,
                  value: formatNumber(record.value),
                  project: projectName,
                });
              }
            });
          }
        });

        metricsMap[projectName] = rows;
      });

    setMetrics(metricsMap);
  }, [year, semiAnnual, selectedProjects, invit]);

  const toggleProjectSelection = (project: string) => {
    const isSelected = selectedProjects.includes(project);

    if (isSelected) {
      if (selectedProjects.length === 1) {
        toast.warning("At least one project must remain selected!");
        setDropdownOpen(false);
        return;
      }
      setSelectedProjects((prev) => prev.filter((p) => p !== project));
    } else if (selectedProjects.length < 3) {
      setSelectedProjects((prev) => [...prev, project]);
    } else {
      toast.warning("You can select a maximum of 3 projects!");
    }

    // Close dropdown automatically after selection change
    setDropdownOpen(false);
  };

  return (
    <section
      className="metrics"
      onClick={(e) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          setDropdownOpen(false);
        }
      }}
    >
      <div className="metrics__card">
        {/* Header */}
        <div className="metrics__header">
          <h2>Operational Metrics</h2>

          <div className="metrics__subheader">
            <div className="metrics__dropdown-group">
              <div className="metrics__dropdown-1">
                <select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="metrics__dropdown-1">
                <select
                  value={semiAnnual}
                  onChange={(e) => setSemiAnnual(e.target.value)}
                >
                  {semiAnnuals.map((s, idx) => (
                    <option key={idx} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              ref={dropdownRef}
              className="metrics__custom-dropdown"
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              <div className="metrics__dropdown-display">
                {selectedProjects.length > 0
                  ? selectedProjects.join(", ")
                  : "Select Projects"}
                <span className={`arrow ${dropdownOpen ? "open" : ""}`}>▼</span>
              </div>

              {dropdownOpen && (
                <div
                  className="metrics__dropdown-menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {projects.map((p) => (
                    <label
                      key={p}
                      className="metrics__option"
                      onClick={() => toggleProjectSelection(p)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProjects.includes(p)}
                        readOnly
                      />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="metrics__table">
          <table>
            <thead>
              <tr>
                <th>Parameters</th>
                {selectedProjects.map((proj, idx) => (
                  <th key={idx}>{proj}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const allMetrics = Object.values(metrics).flat();
                const uniqueMetrics = Array.from(
                  new Set(allMetrics.map((m) => m.metric))
                );
                return uniqueMetrics.map((metricName, idx) => (
                  <tr key={idx}>
                    <td>{metricName}</td>
                    {selectedProjects.map((proj, i) => {
                      const row = metrics[proj]?.find(
                        (m) => m.metric === metricName
                      );
                      return <td key={i}>{row ? safeFormat(row.value) : "—"}</td>;
                    })}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Operational;
